import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Drawer } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconCircleFilled,
  IconClock,
  IconMenu2,
  IconSend,
} from '@tabler/icons-react';
import type { ExamSubmittedState } from './ExamSubmittedPage';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/common/Modal';
import { toast } from '../../components/common/Toaster';
import { useExamSecurity } from '../../hooks/useExamSecurity';
import { ExamPortalHeader } from './components/ExamPortalHeader';
import { QuestionCard } from './components/QuestionCard';
import { QuestionNavigation } from './components/QuestionNavigation';
import type { ExamQuestion } from './components/types';
import apiService from '../../services/ApiService';
import {
  getRegistrationId,
  getRollNo,
  getUserName,
  getExamEndTime,
  setUserSession,
} from '../../services/authSession';
import type { SampleExamApiQuestion } from '../../services/sampleExamTypes';
import { clearAuthAndRedirectToLogin } from '../../services/authNavigation';
import {
  getStoredRegularExamSessionId,
  setStoredRegularExamSessionId,
} from '../../services/regularExamStartRecord';
import { mapSampleExamQuestionsToQuestions } from './utils/mapSampleExamQuestions';
import { buildFullQuestionAnswerMap, buildQuestionAnswerEntry } from './utils/examAnswerPayload';
import { examQuestionUiType, initialAnswerFromStudentField } from './utils/examQuestionUi';
import {
  isMatchPairCorrect,
  normalizeToken,
  parseMatchPairAnswer,
  resolveExpectedPairs,
} from './utils/matchPairAnswer';
import { useCameraContext } from '../../contexts/CameraContext';
import { useAntiCheat } from '../../hooks/useAntiCheat';
import AnswerOfflineStorageService from '../../services/AnswerOfflineStorageService';
import Footer from '../../components/layout/Footer';
import classes from './css/ExamPage.module.css';

const SAMPLE_EXAM_DURATION_SECONDS = 10 * 60;
const SECURITY_VIOLATION_LIMIT = 5;

function isQuestionAnswered(value: any, question?: ExamQuestion): boolean {
  if (question && examQuestionUiType(question) === 'match_pair') {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) return false;
    return Object.keys(value as Record<string, unknown>).some(
      (k) => String((value as Record<string, unknown>)[k] ?? '').trim() !== ''
    );
  }
  if (value == null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

function formatSubmittedAt(): string {
  const now = new Date();
  const d = now.getDate().toString().padStart(2, '0');
  const m = (now.getMonth() + 1).toString().padStart(2, '0');
  const y = now.getFullYear();
  const h = now.getHours().toString().padStart(2, '0');
  const min = now.getMinutes().toString().padStart(2, '0');
  const s = now.getSeconds().toString().padStart(2, '0');
  return `${d}/${m}/${y} ${h}:${min}:${s}`;
}

export default function ExamPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paperIdParam = searchParams.get('paperId');
  const examModeParam = searchParams.get('mode');
  const hasPaperIdInitially =
    typeof window !== 'undefined' &&
    Boolean(new URLSearchParams(window.location.search).get('paperId'));
  const sessionIdParam = searchParams.get('sessionId');

  const regularSessionId = useMemo(() => {
    if (examModeParam !== 'regular') return null;
    if (sessionIdParam == null || sessionIdParam === '') return null;
    const n = Number(sessionIdParam);
    return Number.isFinite(n) ? n : null;
  }, [examModeParam, sessionIdParam]);

  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(hasPaperIdInitially);
  const [questionsError, setQuestionsError] = useState<string | null>(null);

  const [answers, setAnswers] = useState<Record<number, any>>({});
  /** Regular exam: question ids whose answer was accepted by `exam/answer` (drives green nav). */
  const [serverAnsweredIds, setServerAnsweredIds] = useState<Set<number>>(new Set());
  const [pendingSyncIds, setPendingSyncIds] = useState<Set<number>>(new Set());
  const [flaggedIds, setFlaggedIds] = useState<Set<number>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [navOpen, { open: openNav, close: closeNav }] = useDisclosure(false);
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine
  );
  const [warningOpen, { open: openWarning, close: closeWarning }] = useDisclosure(false);
  const [submitConfirmOpen, { open: openSubmitConfirm, close: closeSubmitConfirm }] =
    useDisclosure(false);
  const [
    autoSubmitLimitOpen,
    { open: openAutoSubmitLimitModal, close: closeAutoSubmitLimitModal },
  ] = useDisclosure(false);
  /** Resolved session for regular exam: URL `sessionId` from dashboard or cached value for same paper. */
  const [examSessionId, setExamSessionId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const [autoSubmitReason, setAutoSubmitReason] = useState<'time' | 'violation' | null>(null);
  const [sampleTimerStartedAtMs, setSampleTimerStartedAtMs] = useState<number | null>(null);
  const [sampleTimeEndedOpen, setSampleTimeEndedOpen] = useState(false);

  const {
    stream,
    error: cameraError,
    isActive: isCameraActive,
    captureScreenshot,
  } = useCameraContext();
  useAntiCheat();

  const handleSubmit = useCallback(async () => {
    closeNav();
    closeWarning();
    closeSubmitConfirm();
    closeAutoSubmitLimitModal();
    setSampleTimeEndedOpen(false);
    if (examModeParam === 'regular') {
      if (examSessionId == null) {
        toast.error('Missing exam session. Please open the exam from the dashboard.');
        return;
      }
      try {
        setSubmitting(true);
        const questionAnswer = buildFullQuestionAnswerMap(questions, answers);
        await apiService.submitRegularExam(examSessionId, questionAnswer);
      } catch (err: any) {
        if (err?.response?.status === 401) {
          clearAuthAndRedirectToLogin('session_invalid');
          return;
        }
        toast.error(err?.response?.data?.msg || err?.message || 'Failed to submit exam.');
        return;
      } finally {
        setSubmitting(false);
      }
    }
    const name = getUserName();
    const screenshot = captureScreenshot();
    const rollFromSession = getRollNo().trim() || getRegistrationId().trim();

    const state: ExamSubmittedState = {
      userName: name || 'Student',
      userNameHindi: '',
      rollNumber: rollFromSession || undefined,
      submittedAt: formatSubmittedAt(),
      screenshot: screenshot ?? undefined,
    };

    if (examModeParam === 'sample') {
      const results: any[] = [];
      let totalScore = 0;
      let totalMax = 0;

      questions.forEach((q, index) => {
        const selectedValue = answers[q.questionId];
        const correctRaw = q.correctAnswer;
        const uiType = examQuestionUiType(q);

        if (uiType === 'match_pair') {
          const pairs = q.matchPairs ?? [];
          const expected = resolveExpectedPairs(correctRaw, pairs) ?? {};
          const selectedMap = parseMatchPairAnswer(selectedValue) ?? {};

          let qScored = 0;
          const expectedKeys = Object.keys(expected);
          for (const left of expectedKeys) {
            if (normalizeToken(selectedMap[left]) === normalizeToken(expected[left])) {
              qScored++;
            }
          }

          totalScore += qScored;
          totalMax += expectedKeys.length;

          results.push({
            uiType: 'match_pair',
            questionId: q.questionId,
            questionNo: index + 1,
            questionText: q.questionText,
            matchPairs: pairs,
            selectedPairs: selectedMap,
            correctPairs: expected,
            isCorrect: qScored === expectedKeys.length && expectedKeys.length > 0,
            scoredMarks: qScored,
            totalMarks: expectedKeys.length,
          });
        } else {
          // Single Choice / Default
          const options = q.options ?? [];
          const correct = normalizeToken(correctRaw);

          // Find the selected option object, ensuring we don't treat an object value as a string
          let selectedOption = null;
          if (selectedValue != null && typeof selectedValue !== 'object') {
            const s = String(selectedValue).trim();
            selectedOption = options.find((opt) => {
              if (s === '') return false;
              return (
                normalizeToken(opt.optionId) === normalizeToken(s) ||
                normalizeToken(opt.optionText) === normalizeToken(s)
              );
            });
          }

          // Find the correct option object
          const correctOption = options.find((opt) => {
            if (correct === '') return false;
            return (
              normalizeToken(opt.optionId) === correct || normalizeToken(opt.optionText) === correct
            );
          });

          // Determine if correct
          let isCorrect = false;
          if (selectedOption && correctOption) {
            isCorrect =
              normalizeToken(selectedOption.optionId) === normalizeToken(correctOption.optionId);
          } else if (selectedOption && !correctOption && correct !== '') {
            isCorrect =
              normalizeToken(selectedOption.optionId) === correct ||
              normalizeToken(selectedOption.optionText) === correct;
          }

          const qScored = isCorrect ? 1 : 0;
          totalScore += qScored;
          totalMax += 1;

          results.push({
            uiType: 'single',
            questionId: q.questionId,
            questionNo: index + 1,
            questionText: q.questionText,
            options,
            selectedOptionId: selectedOption ? String(selectedOption.optionId) : null,
            correctOptionId: correctOption ? String(correctOption.optionId) : null,
            isCorrect,
            scoredMarks: qScored,
            totalMarks: 1,
          });
        }
      });

      state.result = {
        totalQuestions: questions.length,
        score: totalScore,
        maxMarks: totalMax,
        questionResults: results,
      };
    }

    navigate('/exam/submitted', { state });
  }, [
    closeNav,
    closeWarning,
    closeSubmitConfirm,
    closeAutoSubmitLimitModal,
    examModeParam,
    examSessionId,
    navigate,
    questions,
    answers,
    captureScreenshot,
  ]);

  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [timerEndTime, setTimerEndTime] = useState<string | null>(() => getExamEndTime());
  const autoSubmittingTriggeredRef = useRef(false);
  const pendingViolationAutoSubmitRef = useRef(false);

  // Initialize and update timer
  useEffect(() => {
    if (examModeParam === 'sample') {
      if (questionsLoading || !questions.length || sampleTimerStartedAtMs == null) {
        setSecondsLeft(null);
        return;
      }

      const endTime = sampleTimerStartedAtMs + SAMPLE_EXAM_DURATION_SECONDS * 1000;
      const updateTimer = () => {
        const now = Date.now();
        const diff = Math.max(0, Math.floor((endTime - now) / 1000));
        setSecondsLeft(diff);
        if (diff <= 0 && !autoSubmittingTriggeredRef.current) {
          autoSubmittingTriggeredRef.current = true;
          setAutoSubmitReason('time');
          setSampleTimeEndedOpen(true);
        }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }

    if (!timerEndTime || examModeParam !== 'regular') {
      setSecondsLeft(null);
      return;
    }

    const endTime = new Date(timerEndTime).getTime();
    if (isNaN(endTime)) return;

    const updateTimer = () => {
      const now = Date.now();
      const diff = Math.max(0, Math.floor((endTime - now) / 1000));
      setSecondsLeft(diff);

      // Only auto-submit if the timer was actually running and then expired
      // OR if we are sure this is the current active session's end time.
      if (diff <= 0 && !autoSubmittingTriggeredRef.current) {
        // Double check: if timerEndTime is more than a few hours old, ignore it.
        const ageMs = now - new Date(timerEndTime).getTime();
        // If the expiration happened long ago, it might be stale data.
        // But if it just happened (ageMs is positive), we submit.
        if (ageMs < 1000 * 60 * 60 * 2) {
          autoSubmittingTriggeredRef.current = true;
          setAutoSubmitReason('time');
          setIsAutoSubmitting(true);
          // Auto submit
          void handleSubmit();
        }
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [
    examModeParam,
    handleSubmit,
    questions.length,
    questionsLoading,
    sampleTimerStartedAtMs,
    timerEndTime,
  ]);

  useEffect(() => {
    if (!sampleTimeEndedOpen) return;
    const timeout = setTimeout(() => {
      setIsAutoSubmitting(true);
      void handleSubmit();
    }, 1500);
    return () => clearTimeout(timeout);
  }, [sampleTimeEndedOpen, handleSubmit]);

  const formatTime = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const { violations, violationLimit } = useExamSecurity({
    onViolation: (count) => {
      if (count < SECURITY_VIOLATION_LIMIT) openWarning();
    },
    onViolationLimitExceeded: () => {
      // Important: don't submit immediately while user is away or switching fast.
      // Show the "limit reached" modal first; submission is triggered when the tab is visible.
      closeWarning();
      openAutoSubmitLimitModal();
      setAutoSubmitReason('violation');
      setIsAutoSubmitting(true);
      pendingViolationAutoSubmitRef.current = true;
    },
    violationLimit: SECURITY_VIOLATION_LIMIT,
    enabled: !questionsLoading && questions.length > 0,
  });

  useEffect(() => {
    if (!pendingViolationAutoSubmitRef.current) return;
    if (autoSubmittingTriggeredRef.current) return;
    if (!autoSubmitLimitOpen) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const trySubmit = () => {
      // Only submit once the tab is visible, otherwise the user "comes back" to a submitted exam
      // without seeing the warning/limit UI.
      if (document.visibilityState !== 'visible') return;
      if (autoSubmittingTriggeredRef.current) return;
      autoSubmittingTriggeredRef.current = true;
      pendingViolationAutoSubmitRef.current = false;

      timeoutId = setTimeout(() => {
        void handleSubmit();
      }, 3000);
    };

    trySubmit();
    document.addEventListener('visibilitychange', trySubmit);
    return () => {
      document.removeEventListener('visibilitychange', trySubmit);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [autoSubmitLimitOpen, handleSubmit]);

  useEffect(() => {
    let cancelled = false;

    if (!paperIdParam) {
      setExamSessionId(null);
      setQuestions([]);
      setQuestionsError(null);
      setQuestionsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const paperId = Number(paperIdParam);
    if (Number.isNaN(paperId)) {
      setExamSessionId(null);
      setQuestionsError('Invalid exam link.');
      setQuestions([]);
      setQuestionsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setQuestionsLoading(true);
    setQuestionsError(null);
    setExamSessionId(null);
    setSampleTimerStartedAtMs(null);
    setSampleTimeEndedOpen(false);
    autoSubmittingTriggeredRef.current = false;

    const useRegularApi = examModeParam === 'regular';
    const fetchQuestions = useRegularApi
      ? (async () => {
          const sid: number | null = regularSessionId ?? getStoredRegularExamSessionId(paperId);
          if (cancelled) return [];
          if (sid != null) setStoredRegularExamSessionId(paperId, sid);
          setExamSessionId(sid);
          if (sid == null) {
            throw new Error('MISSING_SESSION');
          }
          return apiService.getRegularExamQuestions(paperId);
        })()
      : apiService.getSampleExamQuestions(paperId);

    fetchQuestions
      .then((res) => {
        if (cancelled) return;

        let questionList: SampleExamApiQuestion[] = [];
        if (Array.isArray(res)) {
          questionList = res;
        } else if (res && typeof res === 'object' && 'questions' in res) {
          questionList = res.questions;
          // Sync timer with server-provided remaining time
          if (res.remainingTimeToEnd != null && res.remainingTimeToEnd > 0) {
            const absoluteEnd = new Date(Date.now() + res.remainingTimeToEnd * 1000).toISOString();
            setUserSession(undefined, undefined, undefined, undefined, undefined, absoluteEnd);
            setTimerEndTime(absoluteEnd);
          }
        }

        const mapped = mapSampleExamQuestionsToQuestions(questionList);
        if (!mapped.length) {
          setQuestionsError('No questions were returned for this paper.');
          setQuestions([]);
        } else {
          setQuestions(mapped);
          if (examModeParam === 'sample') {
            setSampleTimerStartedAtMs(Date.now());
          }
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        if (err?.message === 'MISSING_SESSION') {
          setQuestionsError('Missing exam session. Open this exam from the dashboard.');
          setQuestions([]);
          setExamSessionId(null);
          return;
        }
        if (err?.response?.status === 401) {
          clearAuthAndRedirectToLogin('session_invalid');
          return;
        }
        setQuestionsError(
          err?.response?.data?.msg || err?.message || 'Failed to load exam questions.'
        );
        setQuestions([]);
      })
      .finally(() => {
        if (!cancelled) setQuestionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [paperIdParam, examModeParam, regularSessionId]);

  const questionIdsKey = useMemo(() => questions.map((q) => q.questionId).join(','), [questions]);

  useEffect(() => {
    const initial: Record<number, unknown> = {};
    const serverAnswered = new Set<number>();
    for (const q of questions) {
      const a = initialAnswerFromStudentField(q);
      if (a !== undefined) {
        initial[q.questionId] = a;
        if (
          examModeParam === 'regular' &&
          q.studentAnswer != null &&
          String(q.studentAnswer).trim() !== ''
        ) {
          serverAnswered.add(q.questionId);
        }
      }
    }

    // Restore any pending unsent answers from localStorage
    if (examSessionId != null) {
      const pending = AnswerOfflineStorageService.getPendingAnswers(examSessionId);
      for (const [qid, data] of Object.entries(pending)) {
        const qidNum = Number(qid);
        const q = questions.find((x) => x.questionId === qidNum);
        if (q) {
          if ('answer' in data.entry) {
            // Find optionId that matches this text for selection-type questions
            const opt = q.options?.find(
              (o) => String(o.optionText).trim() === String((data.entry as any).answer).trim()
            );
            initial[qidNum] = opt ? String(opt.optionId) : (data.entry as any).answer;
          } else if ('matchPairAnswer' in data.entry) {
            initial[qidNum] = data.entry.matchPairAnswer;
          }
          serverAnswered.add(qidNum);
        }
      }
    }

    setAnswers(initial);
    setServerAnsweredIds(serverAnswered);
    const pending =
      examSessionId != null ? AnswerOfflineStorageService.getPendingAnswers(examSessionId) : {};
    setPendingSyncIds(new Set(Object.keys(pending).map(Number)));
    setFlaggedIds(new Set());
    setCurrentIndex(0);
  }, [paperIdParam, examModeParam, questionIdsKey, questions, examSessionId]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (examSessionId != null) {
        void apiService.syncOfflineAnswers(examSessionId).then((synced) => {
          if (synced.length > 0) {
            const syncedNums = synced.map(Number);
            setServerAnsweredIds((prev) => {
              const next = new Set(prev);
              syncedNums.forEach((id) => next.add(id));
              return next;
            });
            setPendingSyncIds((prev) => {
              const next = new Set(prev);
              syncedNums.forEach((id) => next.delete(id));
              return next;
            });
          }
        });
      }
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.error('Internet disconnected. Please check your connection.', {
        autoClose: 6000,
      });
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [examSessionId]);

  // Periodic sync and beforeunload warning
  useEffect(() => {
    if (examSessionId == null) return;

    // Sync every 5 minutes if online
    const interval = setInterval(
      () => {
        if (typeof navigator !== 'undefined' && navigator.onLine) {
          void apiService.syncOfflineAnswers(examSessionId).then((synced) => {
            if (synced.length > 0) {
              const syncedNums = synced.map(Number);
              setServerAnsweredIds((prev) => {
                const next = new Set(prev);
                syncedNums.forEach((id) => next.add(id));
                return next;
              });
              setPendingSyncIds((prev) => {
                const next = new Set(prev);
                syncedNums.forEach((id) => next.delete(id));
                return next;
              });
            }
          });
        }
      },
      5 * 60 * 1000
    );

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasUnsent = AnswerOfflineStorageService.hasPendingAnswers(examSessionId);
      const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;

      if (isOffline || hasUnsent) {
        const msg =
          'You have unsent answers or are offline. Reloading may cause connection issues.';
        e.preventDefault();
        e.returnValue = msg;
        return msg;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [examSessionId]);

  // Sync when navigating questions
  useEffect(() => {
    if (examSessionId != null && questions.length > 0) {
      void apiService.syncOfflineAnswers(examSessionId).then((synced) => {
        if (synced.length > 0) {
          const syncedNums = synced.map(Number);
          setServerAnsweredIds((prev) => {
            const next = new Set(prev);
            syncedNums.forEach((id) => next.add(id));
            return next;
          });
          setPendingSyncIds((prev) => {
            const next = new Set(prev);
            syncedNums.forEach((id) => next.delete(id));
            return next;
          });
        }
      });
    }
  }, [currentIndex, examSessionId, questions.length]);

  const totalQuestions = questions.length;
  const answeredIds = useMemo(
    () =>
      new Set(
        questions
          .filter((q) => isQuestionAnswered(answers[q.questionId], q))
          .map((q) => q.questionId)
      ),
    [answers, questions]
  );
  const questionIds = useMemo(() => questions.map((q) => q.questionId), [questions]);
  /** Nav “Answered” green: regular = saved on server; sample = has local answer. */
  const navigationAnsweredIds = useMemo(() => {
    if (examModeParam === 'regular') return serverAnsweredIds;
    return answeredIds;
  }, [examModeParam, serverAnsweredIds, answeredIds]);
  /** Header / summary count: uses local answers to update immediately. */
  const answeredCount = useMemo(
    () => questionIds.filter((id) => answeredIds.has(id)).length,
    [questionIds, answeredIds]
  );
  /** Only flag ids that belong to the current paper’s questions. */
  const flaggedCount = useMemo(
    () => questionIds.filter((id) => flaggedIds.has(id)).length,
    [questionIds, flaggedIds]
  );
  const questionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    questionRefs.current[currentIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [currentIndex]);

  const setAnswer = useCallback(
    (questionId: number, label: any) => {
      setAnswers((prev) => ({ ...prev, [questionId]: label }));
      if (examModeParam !== 'regular' || examSessionId == null) return;
      const q = questions.find((x) => x.questionId === questionId);
      const entry = buildQuestionAnswerEntry(q, label);
      if (entry == null) return;
      void apiService
        .postExamAnswer(examSessionId, { [String(questionId)]: entry })
        .then(() => {
          setServerAnsweredIds((prev) => new Set(prev).add(questionId));
          setPendingSyncIds((prev) => {
            const next = new Set(prev);
            next.delete(questionId);
            return next;
          });
        })
        .catch((err: any) => {
          if (err?.response?.status === 401) {
            clearAuthAndRedirectToLogin('session_invalid');
            return;
          }

          // If it's a network error, ApiService already saved it to offline storage.
          // We update pendingSyncIds so the UI shows it as "Pending Sync".
          const isNetworkError =
            !err.response ||
            err.code === 'ERR_NETWORK' ||
            err.code === 'ECONNABORTED' ||
            err.message === 'Network Error';

          if (isNetworkError) {
            setPendingSyncIds((prev) => new Set(prev).add(questionId));
          }

          const msg =
            err?.response?.data?.msg || err?.message || 'Could not save your answer. Try again.';
          toast.error(msg);
        });
    },
    [examModeParam, examSessionId, questions]
  );

  const toggleFlag = useCallback((questionId: number) => {
    setFlaggedIds((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  }, []);

  const handleSelectQuestion = useCallback(
    (index: number) => {
      setCurrentIndex(index);
      closeNav();
    },
    [closeNav]
  );

  useEffect(() => {
    if (!navOpen) {
      const clear = () => {
        document.body.style.paddingRight = '';
        document.body.style.marginRight = '';
        document.body.style.overflow = '';
      };
      clear();
      const t = setTimeout(clear, 300);
      return () => clearTimeout(t);
    }
  }, [navOpen]);

  return (
    <div className={classes.page}>
      <ExamPortalHeader />
      <header className={classes.header}>
        <div className={classes.headerLeft}>
          <Button
            className={classes.hamburgerBtn}
            onClick={openNav}
            aria-label="Open question navigation"
          >
            <IconMenu2 size={24} />
          </Button>
          <span className={classes.headerTitle}>Practice Exam</span>
          <span className={classes.headerAnswered}>
            {answeredCount}/{totalQuestions} Answered
          </span>
        </div>
        <div className={classes.headerRight}>
          <span
            className={`${classes.onlineBadge} ${!isOnline ? classes.offlineBadge : ''}`}
            aria-live="polite"
            aria-label={isOnline ? 'Online' : 'Offline'}
          >
            <IconCircleFilled size={8} />
            {isOnline ? 'Online' : 'Offline'}
          </span>
          <span
            className={`${classes.timer} ${secondsLeft !== null && secondsLeft < 300 ? classes.timerUrgent : ''}`}
          >
            {secondsLeft !== null ? formatTime(secondsLeft) : '--:--'}
          </span>
          <Button className={classes.submitBtn} onClick={openSubmitConfirm} disabled={submitting}>
            <IconSend size={18} />
            Submit
          </Button>
        </div>
      </header>

      <main className={classes.main}>
        {questionsLoading ? (
          <div className={classes.examState} role="status">
            Loading exam questions...
          </div>
        ) : questionsError ? (
          <div className={classes.examStateError} role="alert">
            {questionsError}
          </div>
        ) : (
          <>
            <section className={classes.questionsSection}>
              <div className={classes.sectionHeader}>
                <h2 className={classes.sectionTitle}>All Questions</h2>
                <div className={classes.summaryRow}>
                  <span className={`${classes.summaryChip} ${classes.summaryAnswered}`}>
                    {answeredCount} Answered
                  </span>
                  <span className={`${classes.summaryChip} ${classes.summaryFlagged}`}>
                    {flaggedCount} Flagged
                  </span>
                </div>
              </div>
              <div className={classes.questionsList}>
                {questions.map((q, index) => (
                  <div
                    key={q.questionId}
                    id={`question-${q.questionId}`}
                    className={classes.questionItem}
                    ref={(el) => {
                      questionRefs.current[index] = el;
                    }}
                  >
                    <QuestionCard
                      question={q}
                      displayNumber={index + 1}
                      answer={answers[q.questionId]}
                      onAnswerChange={(value) => setAnswer(q.questionId, value)}
                      flagged={flaggedIds.has(q.questionId)}
                      onFlagToggle={() => toggleFlag(q.questionId)}
                    />
                  </div>
                ))}
              </div>
              <div className={classes.submitSection}>
                <Button
                  className={classes.submitBtnEnd}
                  onClick={openSubmitConfirm}
                  disabled={submitting}
                >
                  <IconSend size={20} />
                  Submit Exam
                </Button>
              </div>
            </section>

            <aside className={classes.sidebar}>
              <QuestionNavigation
                questionIds={questionIds}
                currentIndex={currentIndex}
                answeredIds={navigationAnsweredIds}
                pendingSyncIds={pendingSyncIds}
                flaggedIds={flaggedIds}
                onSelectQuestion={setCurrentIndex}
                onSubmit={openSubmitConfirm}
              />
            </aside>
          </>
        )}
      </main>

      <Drawer
        opened={navOpen}
        onClose={closeNav}
        position="right"
        size="min(320px, 85vw)"
        title="Question Navigation"
        removeScrollProps={{ removeScrollBar: false }}
      >
        <div style={{ padding: '20px 10px' }}>
          <QuestionNavigation
            questionIds={questionIds}
            currentIndex={currentIndex}
            answeredIds={navigationAnsweredIds}
            pendingSyncIds={pendingSyncIds}
            flaggedIds={flaggedIds}
            onSelectQuestion={handleSelectQuestion}
            onSubmit={openSubmitConfirm}
          />
        </div>
      </Drawer>

      {isAutoSubmitting && (
        <div className={classes.autoSubmitOverlay}>
          <div className={classes.autoSubmitContent}>
            <IconClock size={48} className={classes.autoSubmitIcon} />
            <h2>{autoSubmitReason === 'violation' ? 'Security Violation' : 'Time Expired'}</h2>
            <p>
              {autoSubmitReason === 'violation'
                ? 'Too many tab switches detected. Your exam is getting submitted automatically...'
                : 'Your exam is getting submitted automatically...'}
            </p>
          </div>
        </div>
      )}

      <Modal
        opened={sampleTimeEndedOpen}
        onClose={() => {}}
        title={<span className={classes.warningModalTitle}>Time Ended</span>}
        className={classes.warningModal}
        size="sm"
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <p className={classes.warningModalText}>
          Your time has ended. The paper is being auto-submitted.
        </p>
      </Modal>

      <Modal
        opened={submitConfirmOpen}
        onClose={closeSubmitConfirm}
        title={<span className={classes.submitModalTitle}>Submit Exam?</span>}
        className={classes.submitConfirmModal}
        size="sm"
        withCloseButton={true}
      >
        <p className={classes.submitModalText}>Are you sure you want to submit your exam?</p>
        <div className={classes.submitModalSummary}>
          <div className={classes.submitModalSummaryItem}>
            <span className={classes.submitModalSummaryValue}>{answeredCount}</span>
            <span className={classes.submitModalSummaryLabel}>Answered</span>
          </div>
          <div className={classes.submitModalSummaryItem}>
            <span className={classes.submitModalSummaryValue}>
              {totalQuestions - answeredCount}
            </span>
            <span className={classes.submitModalSummaryLabel}>Unanswered</span>
          </div>
        </div>
        <div className={classes.submitModalActions}>
          <Button className={classes.submitModalContinueBtn} onClick={closeSubmitConfirm}>
            Continue Exam
          </Button>
          <Button
            className={classes.submitModalSubmitBtn}
            disabled={submitting}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {submitting ? 'Submitting…' : 'Submit Now'}
          </Button>
        </div>
      </Modal>

      <Modal
        opened={warningOpen}
        onClose={closeWarning}
        title={
          <span className={classes.warningModalTitle}>
            <IconAlertTriangle size={24} aria-hidden />
            Warning: Tab Switch Detected!
          </span>
        }
        className={classes.warningModal}
        size="sm"
        withCloseButton={false}
      >
        <p className={classes.warningModalText}>
          You switched away from the exam window. This action has been recorded.
        </p>
        <p className={classes.warningModalCount}>
          Warning {violations}/{violationLimit}
        </p>
        <p className={classes.warningModalText}>
          Exceeding the limit will result in automatic exam submission.
        </p>
        <Button className={classes.warningModalBtn} onClick={closeWarning}>
          I Understand
        </Button>
      </Modal>

      <Modal
        opened={autoSubmitLimitOpen}
        onClose={() => {}}
        title={
          <span className={classes.warningModalTitle}>
            <IconAlertTriangle size={24} aria-hidden />
            Tab switch limit reached
          </span>
        }
        className={classes.warningModal}
        size="sm"
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <p className={classes.warningModalText}>
          Your paper is auto-submitting now as you have reached the tab switch limit.
        </p>
        <Button
          className={classes.warningModalBtn}
          disabled={submitting}
          onClick={() => {
            void handleSubmit();
          }}
        >
          {submitting ? 'Submitting…' : 'OK'}
        </Button>
      </Modal>
      <Footer />
    </div>
  );
}
